import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import {
  CreditCard,
  Download,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle,
  FileText,
  ArrowUp,
  ArrowDown,
  ArrowLeft,
  LogOut,
  Home,
  Settings,
  FileText as Receipt
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { AccountCreditsCard } from '../components/AccountCreditsCard';
import axios from 'axios';

const planBenefitVector = (plan: any): number[] => {
  if (!plan) return [0, 0, 0, 0, 0, 0, 0, 0];
  return [
    plan.has_crm ? 1 : 0,
    plan.has_custom_smtp ? 1 : 0,
    plan.has_advanced_reports ? 1 : 0,
    plan.has_api_access ? 1 : 0,
    Number(plan.max_stores || 0),
    Number(plan.max_employees_per_store || 0),
    Number(plan.max_customers || 0),
    Number(plan.max_rewards || 0),
  ];
};

const comparePlanBenefits = (targetPlan: any, currentPlan: any): number => {
  const target = planBenefitVector(targetPlan);
  const current = planBenefitVector(currentPlan);
  const length = Math.max(target.length, current.length);

  for (let i = 0; i < length; i++) {
    const left = target[i] ?? 0;
    const right = current[i] ?? 0;
    if (left !== right) return left > right ? 1 : -1;
  }

  return 0;
};

const DashboardContainer = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
`;

const Header = styled.header`
  background: white;
  padding: 16px 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 32px;

  @media (max-width: 768px) {
    padding: 12px 0;
    margin-bottom: 20px;
  }
`;

const HeaderContent = styled.div`
  width: 100%;
  max-width: none;
  margin: 0;
  padding: 0 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 12px;
    align-items: flex-start;
    padding: 0 16px;
  }
`;

const Logo = styled.h1`
  color: #00d4aa;
  font-size: 24px;
  font-weight: 700;
  cursor: pointer;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const Nav = styled.nav`
  display: flex;
  gap: 16px;
  align-items: center;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    width: 100%;
    gap: 10px;
  }
`;

const NavButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: transparent;
  color: #475569;
  border-radius: 6px;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    background: #f1f5f9;
    color: #00d4aa;
  }

  @media (max-width: 768px) {
    flex: 1 1 140px;
    justify-content: center;
  }
`;

const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #dc3545;
  color: white;
  border-radius: 6px;
  font-weight: 500;
  transition: background 0.2s ease;

  &:hover {
    background: #c82333;
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

const PageContainer = styled.div`
  padding: 24px;
  width: 100%;
  max-width: none;
  margin: 0;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const PageHeader = styled.div`
  margin-bottom: 32px;

  @media (max-width: 768px) {
    margin-bottom: 20px;
  }
`;

const PageHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 12px;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 12px;
  }
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  color: #6c757d;
  font-size: 14px;
  transition: all 0.2s ease;

  &:hover {
    background: #f8f9fa;
    color: #495057;
  }
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 8px;

  @media (max-width: 768px) {
    font-size: 22px;
  }
`;

const Subtitle = styled.p`
  color: #64748b;
  font-size: 14px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
  margin-bottom: 32px;
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`;

const CardTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #1a202c;
`;

const Badge = styled.span<{ $variant?: string }>`
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background: ${props => {
    switch(props.$variant) {
      case 'success': return '#d1fae5';
      case 'warning': return '#fed7aa';
      case 'danger': return '#fee2e2';
      case 'info': return '#dbeafe';
      default: return '#e2e8f0';
    }
  }};
  color: ${props => {
    switch(props.$variant) {
      case 'success': return '#065f46';
      case 'warning': return '#92400e';
      case 'danger': return '#991b1b';
      case 'info': return '#1e40af';
      default: return '#475569';
    }
  }};
`;

const PlanInfo = styled.div`
  margin-bottom: 24px;
`;

const PlanName = styled.h3`
  font-size: 24px;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 8px;
`;

const PlanPrice = styled.div`
  font-size: 36px;
  font-weight: 700;
  color: #3b82f6;
  margin-bottom: 4px;

  span {
    font-size: 18px;
    color: #64748b;
  }
`;

const PlanFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 20px 0;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
  color: #475569;
  font-size: 14px;

  svg {
    color: #22c55e;
    flex-shrink: 0;
  }
`;

const ProgressBar = styled.div`
  margin: 16px 0;
`;

const ProgressLabel = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 14px;
  color: #475569;
`;

const ProgressTrack = styled.div`
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
`;

const ProgressFill = styled.div<{ $percentage: number; $isOverLimit?: boolean }>`
  height: 100%;
  width: ${props => Math.min(props.$percentage, 100)}%;
  background: ${props => props.$isOverLimit ? '#ef4444' : props.$percentage > 80 ? '#f59e0b' : '#3b82f6'};
  transition: width 0.3s ease;
`;

const Button = styled.button<{ $variant?: string; $fullWidth?: boolean }>`
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  width: ${props => props.$fullWidth ? '100%' : 'auto'};

  ${props => {
    switch(props.$variant) {
      case 'primary':
        return `
          background: #3b82f6;
          color: white;
          &:hover:not(:disabled) { background: #2563eb; }
        `;
      case 'danger':
        return `
          background: #ef4444;
          color: white;
          &:hover:not(:disabled) { background: #dc2626; }
        `;
      case 'outline':
        return `
          background: white;
          border: 2px solid #e2e8f0;
          color: #475569;
          &:hover:not(:disabled) { border-color: #cbd5e1; background: #f8fafc; }
        `;
      default:
        return `
          background: #f1f5f9;
          color: #475569;
          &:hover:not(:disabled) { background: #e2e8f0; }
        `;
    }
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  @media (max-width: 768px) {
    min-width: 720px;
  }
`;

const Th = styled.th`
  text-align: left;
  padding: 12px;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  border-bottom: 2px solid #e2e8f0;
`;

const Td = styled.td`
  padding: 16px 12px;
  border-bottom: 1px solid #f1f5f9;
  font-size: 14px;
  color: #475569;
`;

const StatusIcon = styled.span<{ $status: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;

  ${props => {
    switch(props.$status) {
      case 'paid':
        return `background: #d1fae5; color: #065f46;`;
      case 'pending':
        return `background: #fed7aa; color: #92400e;`;
      case 'overdue':
        return `background: #fee2e2; color: #991b1b;`;
      default:
        return `background: #e2e8f0; color: #475569;`;
    }
  }}
`;

const TabContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
  border-bottom: 2px solid #e2e8f0;

  @media (max-width: 768px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: 12px 24px;
  font-weight: 600;
  font-size: 14px;
  border-bottom: 2px solid ${props => props.$active ? '#3b82f6' : 'transparent'};
  color: ${props => props.$active ? '#3b82f6' : '#64748b'};
  margin-bottom: -2px;
  transition: all 0.2s ease;

  &:hover {
    color: #3b82f6;
  }

  @media (max-width: 768px) {
    padding: 10px 16px;
    white-space: nowrap;
  }
`;

const TableWrapper = styled.div`
  @media (max-width: 768px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: #94a3b8;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 32px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
`;

const ModalHeader = styled.div`
  margin-bottom: 24px;
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 8px;
`;

const ModalDescription = styled.p`
  color: #64748b;
  font-size: 14px;
  line-height: 1.5;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  margin-bottom: 24px;

  @media (min-width: 768px) {
    grid-template-columns: 2fr 1fr;
  }
`;

const FormGroup = styled.div<{ $fullWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  grid-column: ${props => props.$fullWidth ? '1 / -1' : 'auto'};
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #475569;
`;

const Input = styled.input`
  padding: 10px 14px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }

  &::placeholder {
    color: #cbd5e1;
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
`;

const WarningBox = styled.div`
  background: #fef3c7;
  border: 1px solid #fbbf24;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 16px;
  font-size: 13px;
  color: #92400e;
  line-height: 1.5;

  strong {
    display: block;
    margin-bottom: 8px;
    font-weight: 600;
  }

  ul {
    margin: 8px 0 0 0;
    padding-left: 20px;
  }

  li {
    margin: 4px 0;
  }
`;

const PlansGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
  margin-top: 24px;
`;

const PlanCard = styled(Card)<{ $isCurrentPlan?: boolean; $isRecommended?: boolean }>`
  position: relative;
  border: 2px solid ${props => props.$isCurrentPlan ? '#3b82f6' : props.$isRecommended ? '#22c55e' : 'transparent'};

  ${props => props.$isRecommended && `
    &::before {
      content: 'Recomendado';
      position: absolute;
      top: -12px;
      right: 24px;
      background: #22c55e;
      color: white;
      padding: 4px 12px;
      border-radius: 12px;
      font-size: 12px;
      font-weight: 600;
    }
  `}
`;

export const BillingPage: React.FC = () => {
  const { logout } = useAuth();
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<'overview' | 'invoices' | 'plans'>('overview');
  const [loading, setLoading] = useState(true);
  const [billingData, setBillingData] = useState<any>(null);
  const [plans, setPlans] = useState<any[]>([]);
  const [processingAction, setProcessingAction] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [pendingUpgradePlanId, setPendingUpgradePlanId] = useState<number | null>(null);
  const [addressForm, setAddressForm] = useState({
    address_street: '',
    address_number: '',
    address_neighborhood: '',
    address_city: '',
    address_state: '',
    address_zip_code: '',
    address_complement: ''
  });

  const loadBillingData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL || '/api'}/billing`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setBillingData(response.data.data);
      }
    } catch (error: any) {
      console.error('Error loading billing data:', error);
      
      if (error.response?.status === 404) {
        showError(error.response?.data?.message || 'Nenhuma empresa encontrada. Por favor, cadastre uma empresa primeiro.');
        
        // Se houver sugestão de redirecionamento, pode usar aqui
        if (error.response?.data?.redirect) {
          setTimeout(() => {
            navigate(error.response.data.redirect);
          }, 3000);
        }
      } else {
        showError(error.response?.data?.message || 'Erro ao carregar dados de cobrança');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate, showError]);

  const loadPlans = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL || '/api'}/plans`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setPlans(response.data.data);
      }
    } catch (error) {
      console.error('Error loading plans:', error);
    }
  }, []);

  useEffect(() => {
    loadBillingData();
    loadPlans();
  }, [loadBillingData, loadPlans]);

  const handleUpgrade = async (planId: number) => {
    const selectedPlan = plans.find(p => p.id === planId);

    if (!selectedPlan) {
      showError('Plano não encontrado');
      return;
    }

    try {
      setProcessingAction(true);
      const token = localStorage.getItem('auth_token');

      // Chamar endpoint de upgrade que calcula o valor proporcional
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || '/api'}/billing/upgrade`,
        { plan_id: planId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        const { prorate_details, init_point } = response.data.data;
        const paidWithCredits = response.data.data?.paid_with_credits === true;

        // Mostrar detalhes do upgrade antes de redirecionar
        if (paidWithCredits || !init_point) {
          await Swal.fire({
            title: 'Upgrade aplicado',
            text: response.data.message || 'Seu plano foi atualizado com sucesso.',
            icon: 'success',
            confirmButtonColor: '#00d4aa'
          });
          await loadBillingData();
          return;
        }

        const result = await Swal.fire({
          title: 'Confirmar Upgrade',
          html: `
            <div style="text-align: left; margin: 16px 0;">
              <p style="margin-bottom: 12px;">
                Você será redirecionado para o MercadoPago para realizar o pagamento do upgrade de
                <strong>${prorate_details.previous_plan}</strong> para <strong>${prorate_details.new_plan}</strong>.
              </p>

              <div style="background: #f8f9fa; padding: 12px; border-radius: 8px; margin-top: 12px;">
                <p style="margin: 4px 0;"><strong>Detalhes da Cobrança Proporcional:</strong></p>
                <p style="margin: 4px 0;">Dias restantes: ${Math.round(prorate_details.days_remaining)} dias</p>
                <p style="margin: 4px 0;">Novo plano (proporcional): R$ ${prorate_details.new_plan_prorated.toFixed(2)}</p>
                <p style="margin: 4px 0;">Crédito do plano anterior: -R$ ${prorate_details.current_plan_credit.toFixed(2)}</p>
                <p style="margin: 8px 0; font-size: 16px; color: #00d4aa;"><strong>Total a pagar: R$ ${prorate_details.total_to_pay.toFixed(2)}</strong></p>
              </div>

              <p style="margin-top: 12px; color: #64748b; font-size: 14px;">
                Após a confirmação do pagamento, seu plano será atualizado automaticamente.
              </p>
            </div>
          `,
          icon: 'question',
          showCancelButton: true,
          confirmButtonColor: '#00d4aa',
          cancelButtonColor: '#6c757d',
          confirmButtonText: 'Ir para Pagamento',
          cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed && init_point) {
          // Redirecionar para o MercadoPago
          window.location.href = init_point;
        }
      }
    } catch (error: any) {
      // Verificar se precisa completar o endereço
      if (error.response?.data?.needs_address === true) {
        setPendingUpgradePlanId(planId);
        setShowAddressModal(true);
      } else {
        showError(error.response?.data?.message || 'Erro ao processar upgrade');
      }
    } finally {
      setProcessingAction(false);
    }
  };

  const handleCepBlur = async () => {
    const cep = addressForm.address_zip_code.replace(/\D/g, '');

    if (cep.length !== 8) {
      return;
    }

    try {
      const response = await axios.get(`https://viacep.com.br/ws/${cep}/json/`);

      if (response.data.erro) {
        showError('CEP não encontrado');
        return;
      }

      setAddressForm({
        ...addressForm,
        address_street: response.data.logradouro || '',
        address_neighborhood: response.data.bairro || '',
        address_city: response.data.localidade || '',
        address_state: response.data.uf || '',
      });

      showSuccess('Endereço encontrado!');
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
    }
  };

  const handleAddressSubmit = async () => {
    try {
      setProcessingAction(true);
      const token = localStorage.getItem('auth_token');

      // Atualizar endereço da empresa
      await axios.put(
        `${process.env.REACT_APP_API_URL || '/api'}/billing/company/address`,
        addressForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showSuccess('Endereço atualizado com sucesso!');
      setShowAddressModal(false);

      // Resetar formulário
      setAddressForm({
        address_street: '',
        address_number: '',
        address_neighborhood: '',
        address_city: '',
        address_state: '',
        address_zip_code: '',
        address_complement: ''
      });

      // Tentar fazer o upgrade novamente
      if (pendingUpgradePlanId) {
        handleUpgrade(pendingUpgradePlanId);
        setPendingUpgradePlanId(null);
      }
    } catch (error: any) {
      showError(error.response?.data?.message || 'Erro ao atualizar endereço');
    } finally {
      setProcessingAction(false);
    }
  };

  const handlePayInvoice = async (invoiceId: number) => {
    try {
      setProcessingAction(true);
      const token = localStorage.getItem('auth_token');

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || '/api'}/billing/invoices/${invoiceId}/payment-link`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success && response.data.data.init_point) {
        window.location.href = response.data.data.init_point;
      } else {
        showError('Link de pagamento não disponível');
      }
    } catch (error: any) {
      showError(error.response?.data?.message || 'Erro ao buscar link de pagamento');
    } finally {
      setProcessingAction(false);
    }
  };

  const handleDowngrade = async (planId: number) => {
    const result = await Swal.fire({
      title: 'Confirmar Downgrade',
      html: 'O downgrade será aplicado imediatamente.<br/>Deseja continuar?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f59e0b',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sim, continuar',
      cancelButtonText: 'Cancelar'
    });

    if (!result.isConfirmed) return;

    try {
      setProcessingAction(true);
      const token = localStorage.getItem('auth_token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || '/api'}/billing/downgrade`,
        { plan_id: planId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        showSuccess(response.data.message);
        loadBillingData();
      }
    } catch (error: any) {
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        // Se houver limites excedidos, mostrar lista detalhada
        const limitErrors = error.response.data.errors;
        const errorList = limitErrors.map((err: string) => `• ${err}`).join('<br/>');

        await Swal.fire({
          title: 'Não é possível fazer o downgrade',
          html: `
            <div style="text-align: left; margin: 16px 0;">
              <p style="margin-bottom: 12px; font-weight: 500;">
                Você precisa excluir alguns registros antes de fazer o downgrade:
              </p>
              <div style="background: #fff3cd; padding: 12px; border-radius: 8px; margin-top: 12px;">
                ${errorList}
              </div>
            </div>
          `,
          icon: 'warning',
          confirmButtonColor: '#00d4aa',
          confirmButtonText: 'Entendi'
        });
      } else {
        showError(error.response?.data?.message || 'Erro ao realizar downgrade');
      }
    } finally {
      setProcessingAction(false);
    }
  };

  const handleCancelSubscription = async () => {
    const result = await Swal.fire({
      title: 'Cancelar Assinatura',
      html: 'Tem certeza que deseja cancelar sua assinatura?<br/><strong>Esta ação não pode ser desfeita!</strong>',
      icon: 'error',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sim, cancelar assinatura',
      cancelButtonText: 'Não, manter assinatura'
    });

    if (!result.isConfirmed) return;

    try {
      setProcessingAction(true);
      const token = localStorage.getItem('auth_token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || '/api'}/billing/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        showSuccess(response.data.message);
        loadBillingData();
      }
    } catch (error: any) {
      showError(error.response?.data?.message || 'Erro ao cancelar assinatura');
    } finally {
      setProcessingAction(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch(status) {
      case 'paid':
        return <CheckCircle size={16} />;
      case 'pending':
        return <Clock size={16} />;
      case 'overdue':
        return <AlertCircle size={16} />;
      default:
        return <XCircle size={16} />;
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('pt-BR');
  };

  if (loading) {
    return (
      <DashboardContainer>
        <Header>
          <HeaderContent>
            <Logo onClick={() => navigate('/dashboard')}>PontoFiel</Logo>
            <Nav>
              <NavButton onClick={() => navigate('/dashboard')}>
                <Home size={16} />
                Dashboard
              </NavButton>
              <LogoutButton onClick={logout}>
                <LogOut size={16} />
                Sair
              </LogoutButton>
            </Nav>
          </HeaderContent>
        </Header>
        <PageContainer>
          <EmptyState>Carregando...</EmptyState>
        </PageContainer>
      </DashboardContainer>
    );
  }

  return (
    <DashboardContainer>
      <Header>
        <HeaderContent>
          <Logo onClick={() => navigate('/dashboard')}>PontoFiel</Logo>
          <Nav>
            <NavButton onClick={() => navigate('/dashboard')}>
              <Home size={16} />
              Dashboard
            </NavButton>
            <LogoutButton onClick={logout}>
              <LogOut size={16} />
              Sair
            </LogoutButton>
          </Nav>
        </HeaderContent>
      </Header>
      <PageContainer>
        <PageHeader>
          <PageHeaderRow>
            <BackButton type="button" onClick={() => navigate('/dashboard')}>
              <ArrowLeft size={16} />
              Voltar ao dashboard
            </BackButton>
            <div>
              <Title>Gerenciamento Financeiro</Title>
              <Subtitle>Gerencie sua assinatura, faturas e pagamentos</Subtitle>
            </div>
          </PageHeaderRow>
        </PageHeader>

        <TabContainer>
          <Tab $active={activeTab === 'overview'} onClick={() => setActiveTab('overview')}>
            Visão Geral
          </Tab>
          <Tab $active={activeTab === 'invoices'} onClick={() => setActiveTab('invoices')}>
            Faturas
          </Tab>
          <Tab $active={activeTab === 'plans'} onClick={() => setActiveTab('plans')}>
            Planos
          </Tab>
        </TabContainer>

        {activeTab === 'overview' && billingData && (
          <>
            <Grid>
              <Card>
                <CardHeader>
                  <CardTitle>Plano Atual</CardTitle>
                  <Badge $variant={billingData.subscription?.status === 'active' ? 'success' : 'warning'}>
                    {billingData.subscription?.status === 'active' ? 'Ativo' : billingData.subscription?.status}
                  </Badge>
                </CardHeader>

                {billingData.subscription ? (
                  <>
                    <PlanInfo>
                      <PlanName>
                        {billingData.subscription.plan.name}
                      </PlanName>
                      <PlanPrice>
                        {formatCurrency(billingData.subscription.plan.price)}
                        <span>/mês</span>
                      </PlanPrice>
                    </PlanInfo>

                    {billingData.subscription.next_billing_date && (
                      <div style={{ marginBottom: '16px' }}>
                        <ProgressLabel>
                          <span>Próxima cobrança</span>
                          <strong>
                            {formatDate(billingData.subscription.next_billing_date)}
                          </strong>
                        </ProgressLabel>
                      </div>
                    )}

                    <div style={{ display: 'grid', gap: '8px' }}>
                      <Button $variant="outline" $fullWidth onClick={() => setActiveTab('plans')}>
                        Alterar Plano
                      </Button>

                      <Button $variant="outline" $fullWidth onClick={() => navigate('/billing/subscription')}>
                        <Settings size={16} style={{ marginRight: '8px' }} />
                        Gerenciar Assinatura
                      </Button>

                      <Button
                        $variant="outline"
                        $fullWidth
                        onClick={() => {
                          if (billingData.subscription.id) {
                            navigate(`/billing/invoices/${billingData.subscription.id}`);
                          } else {
                            showError('Assinatura não encontrada para este histórico.');
                          }
                        }}
                      >
                        <Receipt size={16} style={{ marginRight: '8px' }} />
                        Histórico de Faturas
                      </Button>

                      <Button $variant="outline" $fullWidth onClick={() => navigate('/billing/cards')}>
                        <CreditCard size={16} style={{ marginRight: '8px' }} />
                        Gerenciar Cartões
                      </Button>
                    </div>
                  </>
                ) : (
                  <div style={{ padding: '24px', textAlign: 'center', color: '#64748b' }}>
                    <p style={{ marginBottom: '16px' }}>Nenhum plano ativo no momento</p>
                    <Button $variant="primary" $fullWidth onClick={() => setActiveTab('plans')}>
                      Escolher um Plano
                    </Button>
                  </div>
                )}
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Estatísticas</CardTitle>
                </CardHeader>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#64748b', fontSize: '14px' }}>Total Gasto</span>
                    <strong style={{ fontSize: '20px', color: '#1a202c' }}>
                      {formatCurrency(billingData.stats.total_spent)}
                    </strong>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#64748b', fontSize: '14px' }}>Faturas Pagas</span>
                    <strong style={{ fontSize: '20px', color: '#22c55e' }}>
                      {billingData.stats.paid_invoices}
                    </strong>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#64748b', fontSize: '14px' }}>Faturas Pendentes</span>
                    <strong style={{ fontSize: '20px', color: '#f59e0b' }}>
                      {billingData.stats.pending_invoices}
                    </strong>
                  </div>

                  {billingData.stats.overdue_invoices > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: '#64748b', fontSize: '14px' }}>Faturas Vencidas</span>
                      <strong style={{ fontSize: '20px', color: '#ef4444' }}>
                        {billingData.stats.overdue_invoices}
                      </strong>
                    </div>
                  )}
                </div>
              </Card>
            </Grid>

            <AccountCreditsCard />

            {billingData.subscription && billingData.subscription.plan && (
              <Card>
                <CardHeader>
                  <CardTitle>Uso do Plano</CardTitle>
                </CardHeader>

                {billingData.usage && Object.keys(billingData.usage).length > 0 ? (
                  <>
                    {billingData.usage.stores && (
                      <ProgressBar>
                        <ProgressLabel>
                          <span>Lojas</span>
                          <span>{billingData.usage.stores.used} / {billingData.usage.stores.limit}</span>
                        </ProgressLabel>
                        <ProgressTrack>
                          <ProgressFill
                            $percentage={billingData.usage.stores.percentage}
                            $isOverLimit={billingData.usage.stores.used > billingData.usage.stores.limit}
                          />
                        </ProgressTrack>
                      </ProgressBar>
                    )}

                    {billingData.usage.customers && (
                      <ProgressBar>
                        <ProgressLabel>
                          <span>Clientes</span>
                          <span>{billingData.usage.customers.used} / {billingData.usage.customers.limit}</span>
                        </ProgressLabel>
                        <ProgressTrack>
                          <ProgressFill
                            $percentage={billingData.usage.customers.percentage}
                            $isOverLimit={billingData.usage.customers.used > billingData.usage.customers.limit}
                          />
                        </ProgressTrack>
                      </ProgressBar>
                    )}

                    {billingData.usage.rewards && (
                      <ProgressBar>
                        <ProgressLabel>
                          <span>Prêmios</span>
                          <span>{billingData.usage.rewards.used} / {billingData.usage.rewards.limit}</span>
                        </ProgressLabel>
                        <ProgressTrack>
                          <ProgressFill
                            $percentage={billingData.usage.rewards.percentage}
                            $isOverLimit={billingData.usage.rewards.used > billingData.usage.rewards.limit}
                          />
                        </ProgressTrack>
                      </ProgressBar>
                    )}

                    {billingData.usage.employees && (
                      <ProgressBar>
                        <ProgressLabel>
                          <span>Funcionários</span>
                          <span>{billingData.usage.employees.used} / {billingData.usage.employees.limit}</span>
                        </ProgressLabel>
                        <ProgressTrack>
                          <ProgressFill
                            $percentage={billingData.usage.employees.percentage}
                            $isOverLimit={billingData.usage.employees.used > billingData.usage.employees.limit}
                          />
                        </ProgressTrack>
                      </ProgressBar>
                    )}
                  </>
                ) : (
                  <EmptyState>Carregando informações de uso...</EmptyState>
                )}
              </Card>
            )}

            {billingData.invoices && billingData.invoices.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Últimas Faturas</CardTitle>
                  <Button onClick={() => setActiveTab('invoices')}>Ver Todas</Button>
                </CardHeader>

                <TableWrapper>
                  <Table>
                    <thead>
                      <tr>
                        <Th>Número</Th>
                        <Th>Data</Th>
                        <Th>Valor</Th>
                        <Th>Status</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {billingData.invoices.slice(0, 5).map((invoice: any) => (
                        <tr key={invoice.id}>
                          <Td>{invoice.invoice_number}</Td>
                          <Td>{formatDate(invoice.issue_date)}</Td>
                          <Td>{formatCurrency(invoice.total)}</Td>
                          <Td>
                            <StatusIcon $status={invoice.status}>
                              {getStatusIcon(invoice.status)}
                              {invoice.status === 'paid' ? 'Pago' : invoice.status === 'pending' ? 'Pendente' : 'Vencido'}
                            </StatusIcon>
                          </Td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </TableWrapper>
              </Card>
            )}
          </>
        )}

        {activeTab === 'invoices' && billingData && (
          <Card>
            <CardHeader>
              <CardTitle>Todas as Faturas</CardTitle>
            </CardHeader>

            {billingData.invoices && billingData.invoices.length > 0 ? (
              <TableWrapper>
                <Table>
                  <thead>
                    <tr>
                      <Th>Número</Th>
                      <Th>Plano</Th>
                      <Th>Data de Emissão</Th>
                      <Th>Vencimento</Th>
                      <Th>Valor</Th>
                      <Th>Status</Th>
                      <Th>Ações</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {billingData.invoices.map((invoice: any) => (
                      <tr key={invoice.id}>
                        <Td>{invoice.invoice_number}</Td>
                        <Td>{invoice.plan?.name || '-'}</Td>
                        <Td>{formatDate(invoice.issue_date)}</Td>
                        <Td>{formatDate(invoice.due_date)}</Td>
                        <Td>{formatCurrency(invoice.total)}</Td>
                        <Td>
                          <StatusIcon $status={invoice.status}>
                            {getStatusIcon(invoice.status)}
                            {invoice.status === 'paid' ? 'Pago' : invoice.status === 'pending' ? 'Pendente' : 'Vencido'}
                          </StatusIcon>
                        </Td>
                        <Td>
                          {invoice.status === 'pending' && (
                            <Button
                              $variant="primary"
                              onClick={() => handlePayInvoice(invoice.id)}
                              disabled={processingAction}
                            >
                              Pagar
                            </Button>
                          )}
                          {invoice.status === 'paid' && (
                            <Button $variant="outline">
                              <Download size={16} />
                            </Button>
                          )}
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrapper>
            ) : (
              <EmptyState>
                <FileText size={48} style={{ margin: '0 auto 16px' }} />
                <p>Nenhuma fatura encontrada</p>
              </EmptyState>
            )}
          </Card>
        )}

        {activeTab === 'plans' && (
          <>
            <PlansGrid>
              {plans.filter(p => p.is_active).map((plan: any) => {
                const isCurrentPlan = billingData?.subscription?.plan_id === plan.id;
                const planComparison = comparePlanBenefits(plan, billingData?.subscription?.plan);
                const isUpgrade = planComparison > 0;
                const isDowngrade = planComparison < 0;
                const isEquivalentPlan = !isCurrentPlan && planComparison === 0;

                // Verificar limites para downgrade
                const usage = billingData?.usage || {};
                const limitExceeded = [];

                if (isDowngrade && usage) {
                  if (usage.stores?.used > plan.max_stores) {
                    limitExceeded.push(`Lojas: ${usage.stores.used} cadastrados, mas o plano permite apenas ${plan.max_stores}`);
                  }
                  if (usage.customers?.used > plan.max_customers) {
                    limitExceeded.push(`Clientes: ${usage.customers.used} cadastrados, mas o plano permite apenas ${plan.max_customers}`);
                  }
                  if (usage.rewards?.used > plan.max_rewards) {
                    limitExceeded.push(`Prêmios: ${usage.rewards.used} cadastrados, mas o plano permite apenas ${plan.max_rewards}`);
                  }
                  if (usage.employees?.used > (plan.max_employees_per_store * plan.max_stores)) {
                    limitExceeded.push(`Funcionários: ${usage.employees.used} cadastrados, mas o plano permite apenas ${plan.max_employees_per_store * plan.max_stores}`);
                  }
                }

                return (
                  <PlanCard key={plan.id} $isCurrentPlan={isCurrentPlan} $isRecommended={plan.name === 'Profissional'}>
                    {isCurrentPlan && (
                      <Badge $variant="info" style={{ marginBottom: '16px' }}>
                        Plano Atual
                      </Badge>
                    )}

                    <PlanName>{plan.name}</PlanName>
                    <PlanPrice>
                      {formatCurrency(plan.price)}
                      <span>/mês</span>
                    </PlanPrice>

                    <p style={{ color: '#64748b', fontSize: '14px', margin: '16px 0' }}>
                      {plan.description}
                    </p>

                    <PlanFeatures>
                      <FeatureItem>
                        <CheckCircle size={16} />
                        <span>{plan.max_stores} {plan.max_stores === 1 ? 'Loja' : 'Lojas'}</span>
                      </FeatureItem>
                      <FeatureItem>
                        <CheckCircle size={16} />
                        <span>{plan.max_customers} Clientes</span>
                      </FeatureItem>
                      <FeatureItem>
                        <CheckCircle size={16} />
                        <span>{plan.max_employees_per_store} Funcionários por loja</span>
                      </FeatureItem>
                      <FeatureItem>
                        <CheckCircle size={16} />
                        <span>{plan.max_rewards} Prêmios</span>
                      </FeatureItem>
                      {plan.has_advanced_reports && (
                        <FeatureItem>
                          <CheckCircle size={16} />
                          <span>Relatórios Avançados</span>
                        </FeatureItem>
                      )}
                      {plan.has_api_access && (
                        <FeatureItem>
                          <CheckCircle size={16} />
                          <span>Acesso à API</span>
                        </FeatureItem>
                      )}
                    </PlanFeatures>

                    {isDowngrade && limitExceeded.length > 0 && (
                      <WarningBox>
                        <strong>⚠️ Não é possível fazer downgrade</strong>
                        Você precisa excluir alguns registros:
                        <ul>
                          {limitExceeded.map((error, index) => (
                            <li key={index}>{error}</li>
                          ))}
                        </ul>
                      </WarningBox>
                    )}

                    {!isCurrentPlan && (
                      <Button
                        $variant={isUpgrade ? 'primary' : 'outline'}
                        $fullWidth
                        onClick={() => isUpgrade ? handleUpgrade(plan.id) : handleDowngrade(plan.id)}
                        disabled={processingAction || isEquivalentPlan || (isDowngrade && limitExceeded.length > 0)}
                      >
                        {isUpgrade && <><ArrowUp size={16} style={{ marginRight: '8px' }} /> Fazer Upgrade</>}
                        {isDowngrade && <><ArrowDown size={16} style={{ marginRight: '8px' }} /> Fazer Downgrade</>}
                        {isEquivalentPlan && <>Plano Equivalente</>}
                      </Button>
                    )}

                    {isCurrentPlan && billingData?.subscription?.status === 'active' && (
                      <Button
                        $variant="danger"
                        $fullWidth
                        onClick={handleCancelSubscription}
                        disabled={processingAction}
                      >
                        Cancelar Assinatura
                      </Button>
                    )}
                  </PlanCard>
                );
              })}
            </PlansGrid>
          </>
        )}
      </PageContainer>

      {/* Modal de Endereço */}
      {showAddressModal && (
        <ModalOverlay onClick={() => setShowAddressModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Complete o Endereço da Empresa</ModalTitle>
              <ModalDescription>
                Para continuar com o upgrade, precisamos de algumas informações adicionais do endereço da sua empresa.
              </ModalDescription>
            </ModalHeader>

            <FormGrid>
              <FormGroup $fullWidth>
                <Label>CEP *</Label>
                <Input
                  type="text"
                  placeholder="12345-678"
                  value={addressForm.address_zip_code}
                  onChange={(e) => setAddressForm({ ...addressForm, address_zip_code: e.target.value })}
                  onBlur={handleCepBlur}
                />
              </FormGroup>

              <FormGroup $fullWidth>
                <Label>Rua *</Label>
                <Input
                  type="text"
                  placeholder="Ex: Rua das Flores"
                  value={addressForm.address_street}
                  onChange={(e) => setAddressForm({ ...addressForm, address_street: e.target.value })}
                />
              </FormGroup>

              <FormGroup>
                <Label>Número *</Label>
                <Input
                  type="text"
                  placeholder="123"
                  value={addressForm.address_number}
                  onChange={(e) => setAddressForm({ ...addressForm, address_number: e.target.value })}
                />
              </FormGroup>

              <FormGroup>
                <Label>Complemento (opcional)</Label>
                <Input
                  type="text"
                  placeholder="Sala 101, Bloco A"
                  value={addressForm.address_complement}
                  onChange={(e) => setAddressForm({ ...addressForm, address_complement: e.target.value })}
                />
              </FormGroup>

              <FormGroup>
                <Label>Bairro *</Label>
                <Input
                  type="text"
                  placeholder="Centro"
                  value={addressForm.address_neighborhood}
                  onChange={(e) => setAddressForm({ ...addressForm, address_neighborhood: e.target.value })}
                />
              </FormGroup>

              <FormGroup>
                <Label>Cidade *</Label>
                <Input
                  type="text"
                  placeholder="São Paulo"
                  value={addressForm.address_city}
                  onChange={(e) => setAddressForm({ ...addressForm, address_city: e.target.value })}
                />
              </FormGroup>

              <FormGroup>
                <Label>Estado *</Label>
                <Input
                  type="text"
                  placeholder="SP"
                  maxLength={2}
                  value={addressForm.address_state}
                  onChange={(e) => setAddressForm({ ...addressForm, address_state: e.target.value.toUpperCase() })}
                />
              </FormGroup>
            </FormGrid>

            <ModalActions>
              <Button
                $variant="outline"
                onClick={() => {
                  setShowAddressModal(false);
                  setPendingUpgradePlanId(null);
                }}
                disabled={processingAction}
              >
                Cancelar
              </Button>
              <Button
                $variant="primary"
                onClick={handleAddressSubmit}
                disabled={processingAction || !addressForm.address_street || !addressForm.address_number || !addressForm.address_neighborhood || !addressForm.address_city || !addressForm.address_state || !addressForm.address_zip_code}
              >
                {processingAction ? 'Salvando...' : 'Salvar e Continuar'}
              </Button>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}
    </DashboardContainer>
  );
};
